import React, { MouseEventHandler, useContext, useState } from 'react';
import { Link as RouteLink, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Flex,
    Icon,
    Image,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    Text,
    Box,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerOverlay,
    useDisclosure,
    Divider,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Show
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { AiOutlineLineChart } from 'react-icons/ai';
import { GiSpain, GiPig, GiHamburgerMenu } from 'react-icons/gi';
import packageJson from '../../../../package.json';
import { UserClass } from '../../../core/user';
import { ProfileContext } from '../../../core/contexts';

const activeStyle = {
    borderBottom: '2px solid white',
    display: 'block'
};

const inActiveStyle = {
    display: 'block'
};

export default function Header(): React.ReactElement {
    const { t } = useTranslation();
    const { user, signOut } = useContext(ProfileContext);

    const {
        isOpen: lateralIsOpen,
        onOpen: lateralOnOpen,
        onClose: lateralOnClose
    } = useDisclosure();
    const {
        isOpen: aboutUsIsOpen,
        onOpen: aboutUsOnOpen,
        onClose: aboutUsOnClose
    } = useDisclosure();

    return (
        <header>
            <Flex
                bg='green.800'
                h='60px'
                py={{ base: 1, md: 1 }}
                px={{ base: 1, md: 8 }}
                boxShadow='base'
                align={'center'}
                justify={'space-between'}
            >
                <Flex
                    flex={{ base: 3, md: 11 }}
                    justify={{ base: 'space-around', md: 'flex-start' }}
                >
                    {/* App name */}
                    <Show above='md'>
                        <Box mr='8'>
                            <Flex
                                alignItems='center'
                                onClick={aboutUsOnOpen}
                                cursor='pointer'
                            >
                                <Image
                                    boxSize='40px'
                                    objectFit='cover'
                                    src='/images/icons/icon_clean.png'
                                    alt='Logo'
                                />

                                <Text px='4' py='1' color='white' fontSize='xl'>
                                    {t('global.appName')}
                                </Text>
                            </Flex>
                        </Box>
                    </Show>

                    {/* Lands */}
                    <Box mx='2'>
                        <NavLink
                            to={t('routes.lands')}
                            style={({ isActive }) =>
                                isActive ? activeStyle : inActiveStyle
                            }
                        >
                            <Flex alignItems='center'>
                                <Icon
                                    as={GiSpain}
                                    color='white'
                                    pos='relative'
                                    fontSize='xl'
                                    w='40px'
                                    h='40px'
                                />
                                <Text
                                    px='2'
                                    py='1'
                                    color='white'
                                    fontSize='xl'
                                    display={{ base: 'none', md: 'block' }}
                                >
                                    {t('land.lands')}
                                </Text>
                            </Flex>
                        </NavLink>
                    </Box>

                    {/* Animals */}
                    <Box mx='2'>
                        <NavLink
                            to={t('routes.animals')}
                            style={({ isActive }) =>
                                isActive ? activeStyle : inActiveStyle
                            }
                        >
                            <Flex alignItems='center'>
                                <Icon
                                    as={GiPig}
                                    color='white'
                                    pos='relative'
                                    fontSize='xl'
                                    w='40px'
                                    h='40px'
                                />
                                <Text
                                    px='2'
                                    py='1'
                                    color='white'
                                    fontSize='xl'
                                    display={{ base: 'none', md: 'block' }}
                                >
                                    {t('animal.animals')}
                                </Text>
                            </Flex>
                        </NavLink>
                    </Box>

                    {/* Analysis */}
                    <Box mx='2' display={'flex'}>
                        <NavLink
                            to={t('routes.analysis')}
                            style={({ isActive }) =>
                                isActive ? activeStyle : inActiveStyle
                            }
                        >
                            <Flex alignItems='center'>
                                <Icon
                                    as={AiOutlineLineChart}
                                    color='white'
                                    fontSize='xl'
                                    pos='relative'
                                    w='40px'
                                    h='40px'
                                />
                                <Text
                                    px='2'
                                    py='1'
                                    color='white'
                                    fontSize='xl'
                                    display={{ base: 'none', md: 'block' }}
                                >
                                    {t('analysis.title')}
                                </Text>
                            </Flex>
                        </NavLink>
                    </Box>

                    {/* Lateral menu */}
                    <Box mx='2' display={{ base: 'block', md: 'none' }}>
                        <Flex
                            alignItems='center'
                            verticalAlign={'middle'}
                            cursor='pointer'
                        >
                            <Icon
                                as={GiHamburgerMenu}
                                mt='1'
                                color='white'
                                fontSize='xl'
                                w={9}
                                h={9}
                                onClick={() => lateralOnOpen()}
                            />
                            <Drawer
                                isOpen={lateralIsOpen}
                                placement='right'
                                onClose={lateralOnClose}
                            >
                                <DrawerOverlay />
                                <MobileMenu
                                    onClose={lateralOnClose}
                                    signOut={signOut}
                                    onOpen={aboutUsOnOpen}
                                    user={user}
                                />
                            </Drawer>
                        </Flex>
                    </Box>
                </Flex>

                {/* User Menu */}
                <Box flex='1' display={{ base: 'none', md: 'block' }}>
                    <DesktopMenu user={user} signOut={signOut} />
                </Box>
            </Flex>
            <AboutUsModal
                isOpen={aboutUsIsOpen}
                onOpen={aboutUsOnOpen}
                onClose={aboutUsOnClose}
            />
        </header>
    );
}

const DesktopMenu = (props: {
    user: UserClass;
    signOut: MouseEventHandler<HTMLButtonElement>;
}): React.ReactElement => {
    const { t } = useTranslation();
    const [isMenuOpen, setMenuOpen] = useState(false);

    return (
        <Flex
            mr='6'
            justify={'flex-end'}
            onMouseEnter={() => setMenuOpen(true)}
            onMouseLeave={() => setMenuOpen(false)}
        >
            <Menu isOpen={isMenuOpen}>
                <MenuButton onClick={() => setMenuOpen(true)}>
                    <Flex flexDirection='row' align='center'>
                        <Avatar
                            bg='white'
                            size='md'
                            name={props.user.name}
                            src={props.user.getImage()}
                        />
                        <ChevronDownIcon w={6} h={6} color='white' />
                    </Flex>
                </MenuButton>
                <MenuList p='0' onClick={() => setMenuOpen(false)}>
                    {/* Profile Page */}
                    <RouteLink to={t('routes.profile')}>
                        <MenuItem>
                            <Text py='4'>{t('header.profile')}</Text>
                        </MenuItem>
                    </RouteLink>

                    {/* Users Page */}
                    {props.user.isAdmin() && (
                        <Show above='md'>
                            <RouteLink to={t('routes.users')}>
                                <MenuItem>
                                    <Text py='4'>{t('users.title')}</Text>
                                </MenuItem>
                            </RouteLink>
                        </Show>
                    )}

                    {/* Sign Out */}
                    <MenuDivider m='0' />
                    <MenuItem py='4' onClick={props.signOut}>
                        {t('login.logout')}
                    </MenuItem>
                </MenuList>
            </Menu>
        </Flex>
    );
};

const MobileMenu = (props: {
    user: UserClass;
    signOut: MouseEventHandler<HTMLDivElement>;
    onClose: MouseEventHandler<HTMLAnchorElement | HTMLDivElement>;
    onOpen: MouseEventHandler<HTMLDivElement>;
}): React.ReactElement => {
    const { t } = useTranslation();
    const shareInfo = {
        title: 'Mi Finca',
        text: 'Software para la gestión digital de ganaderías',
        url: 'https://mifinca.web.app'
    };

    const share = () => {
        navigator
            .share(shareInfo)
            .then(() => console.log('Successful share'))
            .catch((error) => {
                console.error('Error sharing', error);
            });
    };

    return (
        <DrawerContent pt='12'>
            <DrawerCloseButton />
            <DrawerBody>
                <Flex direction='column' w='100%' alignItems='left'>
                    <RouteLink to={t('routes.profile')} onClick={props.onClose}>
                        <Text p='4'>{t('header.profile')}</Text>
                        <Divider />
                    </RouteLink>
                    {props.user.isAdmin() && (
                        <RouteLink
                            to={t('routes.users')}
                            onClick={props.onClose}
                        >
                            <Text p='4'>{t('users.title')}</Text>
                            <Divider />
                        </RouteLink>
                    )}

                    {navigator.canShare && navigator.canShare(shareInfo) && (
                        <>
                            <Text onClick={share} p='4'>
                                Invitar
                            </Text>
                            <Divider />
                        </>
                    )}

                    <Text p='4' onClick={props.signOut}>
                        {t('login.logout')}
                    </Text>
                    <Divider />
                </Flex>
            </DrawerBody>

            <DrawerFooter>
                <Flex direction='column' w='100%' alignItems='center'>
                    <Divider my='4' />
                    <Flex
                        cursor={'pointer'}
                        onClick={(e) => {
                            props.onClose(e);
                            props.onOpen(e);
                        }}
                    >
                        <Image
                            boxSize='40px'
                            objectFit='cover'
                            src='/images/icons/icon_clean.png'
                            alt='Logo'
                        />

                        <Text px='4' py='1' fontSize='xl'>
                            {t('global.appName')}
                        </Text>
                    </Flex>
                </Flex>
            </DrawerFooter>
        </DrawerContent>
    );
};

const AboutUsModal = (props: {
    isOpen: boolean;
    onOpen: MouseEventHandler<HTMLButtonElement>;
    onClose: MouseEventHandler<HTMLButtonElement> | any;
}): React.ReactElement => {
    const { t } = useTranslation();
    return (
        <Modal isOpen={props.isOpen} onClose={props.onClose} size='xs'>
            <ModalOverlay />
            <ModalContent>
                <ModalCloseButton />
                <ModalBody py='10'>
                    <Flex direction={'column'} align='center'>
                        <Image
                            boxSize='120px'
                            objectFit='cover'
                            src='/images/icons/icon_clean.png'
                            alt='Logo'
                        />

                        <Text px='4' py='1' fontSize='xl'>
                            {t('global.appName')}
                        </Text>

                        <Text fontSize='sm'>
                            version: {packageJson.version}
                        </Text>

                        <Text fontSize='sm'>mifinca2022app@gmail.com</Text>
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};
